import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import './i18n';

// Auth pages
import LoginPage from './pages/auth/LoginPage';
import SuperAdminLoginPage from './pages/auth/SuperAdminLoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Super Admin pages
import SuperAdminDashboard from './pages/superadmin/Dashboard';
import SuperAdminMosques from './pages/superadmin/Mosques';
import SuperAdminMosqueDetail from './pages/superadmin/MosqueDetail';
import SuperAdminInvoices from './pages/superadmin/Invoices';
import SuperAdminSettings from './pages/superadmin/Settings';
import SuperAdminLogs from './pages/superadmin/ActivityLogs';
import SuperAdminQuestions from './pages/superadmin/Questions';
import SuperAdminMosqueCreate from './pages/superadmin/MosqueCreate';
import SuperAdminBackups from './pages/superadmin/Backups';
import SuperAdminUptime from './pages/superadmin/Uptime';

// Admin pages
import AdminDashboard from './pages/admin/Dashboard';
import AdminQuizzes from './pages/admin/Quizzes';
import AdminQuizDetail from './pages/admin/QuizDetail';
import AdminQuizLobby from './pages/admin/QuizLobby';
import AdminQuestions from './pages/admin/Questions';
import AdminStats from './pages/admin/Statistics';
import AdminProfile from './pages/admin/Profile';
import AdminInvoices from './pages/admin/Invoices';
import AdminQuizLive from './pages/admin/QuizLive';
import AdminQuizSessions from './pages/admin/QuizSessions';
import AdminTournaments from './pages/admin/Tournaments';
import AdminTournamentDetail from './pages/admin/TournamentDetail';
import AdminParticipantHistory from './pages/admin/ParticipantHistory';
import PlanExpiredPage from './pages/admin/PlanExpired';
import NotFound from './pages/NotFound';

// Competitor / Public pages
import JoinPage from './pages/garus/JoinPage';
import LobbyPage from './pages/garus/LobbyPage';
import QuizPage from './pages/garus/QuizPage';
import ResultsPage from './pages/garus/ResultsPage';
import SpectatorPage from './pages/garus/SpectatorPage';
import GuestDisplayPage from './pages/garus/GuestDisplayPage';
import PublicMosquePage from './pages/public/MosquePage';
import InvitePage from './pages/garus/InvitePage';

const PrivateRoute = ({ children, requireRole }) => {
  const { isAuthenticated, isSuperAdmin, isMosqueAdmin, loading } = useAuth();
  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;
  if (!isAuthenticated) return <Navigate to={requireRole === 'super_admin' ? '/superadmin/login' : '/login'} replace />;
  if (requireRole === 'super_admin' && !isSuperAdmin) return <Navigate to="/" replace />;
  if (requireRole === 'mosque' && !isMosqueAdmin) return <Navigate to="/" replace />;
  return children;
};

const HomeRedirect = () => {
  const { isSuperAdmin, isMosqueAdmin, loading } = useAuth();
  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;
  if (isSuperAdmin) return <Navigate to="/superadmin" replace />;
  if (isMosqueAdmin) return <Navigate to="/admin" replace />;
  return <Navigate to="/login" replace />;
};

export default function App() {
  return (
    <AuthProvider>
      <ToastProvider>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
          <Route path="/registrieren" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/join/:pin" element={<JoinPage />} />
          <Route path="/join" element={<JoinPage />} />
          <Route path="/invite/:token" element={<InvitePage />} />
          <Route path="/lobby/:sessionId" element={<LobbyPage />} />
          <Route path="/quiz/:sessionId" element={<QuizPage />} />
          <Route path="/results/:sessionId" element={<ResultsPage />} />
          <Route path="/spectate/:sessionId" element={<SpectatorPage />} />
          <Route path="/guest/:pin" element={<GuestDisplayPage />} />
          <Route path="/mosque/:identifier" element={<PublicMosquePage />} />

          {/* Super Admin */}
          <Route path="/superadmin" element={
            <PrivateRoute requireRole="super_admin"><Navigate to="/superadmin/invoices" replace /></PrivateRoute>
          } />
          <Route path="/superadmin/mosques" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminMosques /></PrivateRoute>
          } />
          <Route path="/superadmin/mosques/new" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminMosqueCreate /></PrivateRoute>
          } />
          <Route path="/superadmin/mosques/:id" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminMosqueDetail /></PrivateRoute>
          } />
          <Route path="/superadmin/questions" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminQuestions /></PrivateRoute>
          } />
          <Route path="/superadmin/invoices" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminInvoices /></PrivateRoute>
          } />
          <Route path="/superadmin/settings" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminSettings /></PrivateRoute>
          } />
          <Route path="/superadmin/logs" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminLogs /></PrivateRoute>
          } />
          <Route path="/superadmin/backups" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminBackups /></PrivateRoute>
          } />
          <Route path="/superadmin/uptime" element={
            <PrivateRoute requireRole="super_admin"><SuperAdminUptime /></PrivateRoute>
          } />

          {/* Mosque Admin */}
          <Route path="/admin" element={
            <PrivateRoute requireRole="mosque"><AdminDashboard /></PrivateRoute>
          } />
          <Route path="/admin/quizzes" element={
            <PrivateRoute requireRole="mosque"><AdminQuizzes /></PrivateRoute>
          } />
          <Route path="/admin/quizzes/:id" element={
            <PrivateRoute requireRole="mosque"><AdminQuizDetail /></PrivateRoute>
          } />
          <Route path="/admin/quizzes/:id/sessions" element={
            <PrivateRoute requireRole="mosque"><AdminQuizSessions /></PrivateRoute>
          } />
          <Route path="/admin/tournaments" element={
            <PrivateRoute requireRole="mosque"><AdminTournaments /></PrivateRoute>
          } />
          <Route path="/admin/tournaments/:id" element={
            <PrivateRoute requireRole="mosque"><AdminTournamentDetail /></PrivateRoute>
          } />
          <Route path="/admin/quizzes/:id/lobby" element={
            <PrivateRoute requireRole="mosque"><AdminQuizLobby /></PrivateRoute>
          } />
          <Route path="/admin/quiz-live/:sessionId" element={
            <PrivateRoute requireRole="mosque"><AdminQuizLive /></PrivateRoute>
          } />
          <Route path="/admin/questions" element={
            <PrivateRoute requireRole="mosque"><AdminQuestions /></PrivateRoute>
          } />
          <Route path="/admin/stats" element={
            <PrivateRoute requireRole="mosque"><AdminStats /></PrivateRoute>
          } />
          <Route path="/admin/invoices" element={
            <PrivateRoute requireRole="mosque"><AdminInvoices /></PrivateRoute>
          } />
          <Route path="/admin/profile" element={
            <PrivateRoute requireRole="mosque"><AdminProfile /></PrivateRoute>
          } />
          <Route path="/admin/change-password" element={
            <PrivateRoute requireRole="mosque"><AdminProfile /></PrivateRoute>
          } />
          <Route path="/admin/participants" element={
            <PrivateRoute requireRole="mosque"><AdminParticipantHistory /></PrivateRoute>
          } />
          <Route path="/admin/plan-expired" element={
            <PrivateRoute requireRole="mosque"><PlanExpiredPage /></PrivateRoute>
          } />

          <Route path="*" element={<NotFound />} />
        </Routes>
      </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  );
}
